import React, { useState, useEffect, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { LoadingSpinner } from '../components/LoadingSpinner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { ScrollArea } from '../components/ui/scroll-area';
import { MessageCircle, Send, ArrowLeft, User } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function MessagesPage() {
  const { partnerId } = useParams();
  const { user, token } = useAuth();
  const [conversations, setConversations] = useState([]);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [selectedPartner, setSelectedPartner] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    fetchConversations();
  }, []);

  useEffect(() => {
    if (partnerId) {
      fetchMessages(partnerId);
    }
  }, [partnerId]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const fetchConversations = async () => {
    try {
      const res = await fetch(`${API}/messages/conversations`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setConversations(data);
        
        // If we have a partnerId, find that partner
        if (partnerId && data.length > 0) {
          const partner = data.find(c => c.partner?.id === partnerId);
          if (partner) setSelectedPartner(partner.partner);
        }
      }
    } catch (e) {
      console.error('Error fetching conversations:', e);
    } finally {
      setLoading(false);
    }
  };

  const fetchMessages = async (pid) => {
    try {
      const res = await fetch(`${API}/messages/${pid}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        setMessages(await res.json());
      }
    } catch (e) {
      console.error('Error fetching messages:', e);
    }
  };

  const handleSend = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || !partnerId) return;

    setSending(true);
    try {
      const res = await fetch(`${API}/messages`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          recipient_id: partnerId,
          content: newMessage.trim()
        })
      });

      if (res.ok) {
        const msg = await res.json();
        setMessages([...messages, msg]);
        setNewMessage('');
        fetchConversations(); // Refresh sidebar
      } else {
        toast.error('Failed to send message');
      }
    } catch (e) {
      toast.error('Error sending message');
    } finally {
      setSending(false);
    }
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner text="Loading messages..." />
      </div>
    );
  }

  return (
    <div className="min-h-[calc(100vh-4rem)] py-8">
      <div className="max-w-6xl mx-auto px-4 md:px-8">
        <h1 className="text-3xl font-heading font-extrabold flex items-center gap-3 mb-8">
          <MessageCircle className="w-8 h-8" />
          Messages
        </h1>

        <div className="grid md:grid-cols-3 gap-6 h-[600px]">
          {/* Conversations List */}
          <div className="card-brutal p-4 overflow-hidden">
            <h2 className="font-semibold mb-4">Conversations</h2>
            <ScrollArea className="h-[520px]">
              {conversations.length > 0 ? (
                <div className="space-y-2">
                  {conversations.map((conv) => (
                    <Link
                      key={conv.partner?.id}
                      to={`/messages/${conv.partner?.id}`}
                      className={`flex items-center gap-3 p-3 rounded-xl transition-colors ${
                        partnerId === conv.partner?.id 
                          ? 'bg-pastel-art/30 border-2 border-primary/20' 
                          : 'hover:bg-muted'
                      }`}
                      onClick={() => setSelectedPartner(conv.partner)}
                      data-testid={`conversation-${conv.partner?.id}`}
                    >
                      <div className="w-10 h-10 rounded-full bg-pastel-stickers/30 flex items-center justify-center flex-shrink-0">
                        <User className="w-5 h-5" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium truncate">{conv.partner?.name}</p>
                        <p className="text-sm text-muted-foreground truncate">
                          {conv.last_message?.content}
                        </p>
                      </div>
                      {conv.unread_count > 0 && (
                        <span className="w-5 h-5 rounded-full bg-pastel-crochet text-xs font-bold flex items-center justify-center">
                          {conv.unread_count}
                        </span>
                      )}
                    </Link>
                  ))}
                </div>
              ) : (
                <p className="text-center text-muted-foreground py-8 text-sm">
                  No conversations yet
                </p>
              )}
            </ScrollArea>
          </div>

          {/* Messages */}
          <div className="md:col-span-2 card-brutal p-4 flex flex-col">
            {partnerId ? (
              <>
                {/* Header */}
                <div className="flex items-center gap-3 pb-4 border-b border-primary/10">
                  <Link to="/messages" className="md:hidden">
                    <Button variant="ghost" size="icon">
                      <ArrowLeft className="w-5 h-5" />
                    </Button>
                  </Link>
                  <div className="w-10 h-10 rounded-full bg-pastel-stickers/30 flex items-center justify-center">
                    <User className="w-5 h-5" />
                  </div>
                  <div>
                    <p className="font-semibold">{selectedPartner?.name || 'Loading...'}</p>
                  </div>
                </div>

                {/* Messages */}
                <ScrollArea className="flex-1 py-4">
                  <div className="space-y-4 px-2">
                    {messages.map((msg) => (
                      <div
                        key={msg.id}
                        className={`flex ${msg.sender_id === user.id ? 'justify-end' : 'justify-start'}`}
                      >
                        <div
                          className={`max-w-[80%] rounded-2xl px-4 py-2 ${
                            msg.sender_id === user.id
                              ? 'bg-primary text-primary-foreground rounded-br-none'
                              : 'bg-muted rounded-bl-none'
                          }`}
                        >
                          <p>{msg.content}</p>
                          <p className={`text-xs mt-1 ${
                            msg.sender_id === user.id ? 'text-primary-foreground/70' : 'text-muted-foreground'
                          }`}>
                            {new Date(msg.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                          </p>
                        </div>
                      </div>
                    ))}
                    <div ref={messagesEndRef} />
                  </div>
                </ScrollArea>

                {/* Input */}
                <form onSubmit={handleSend} className="flex gap-2 pt-4 border-t border-primary/10">
                  <Input
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    placeholder="Type a message..."
                    className="input-brutal flex-1"
                    disabled={sending}
                    data-testid="message-input"
                  />
                  <Button type="submit" disabled={sending || !newMessage.trim()} className="btn-brutal-primary" data-testid="send-message-btn">
                    <Send className="w-5 h-5" />
                  </Button>
                </form>
              </>
            ) : (
              <div className="flex-1 flex items-center justify-center text-muted-foreground">
                <p>Select a conversation to start messaging</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
